import { PaymentRequest } from "@app/models/PaymentRequest";
import { NetworkErrorService } from "@app/services/NetworkError.service";
import { PaymentApi } from "@app/services/apiServices/PaymentApi";
import { SpinnerService } from "@app/services/spinner.service";
import { PaymentStore } from "@app/services/stores/PaymentStore";
import { take } from "rxjs/operators";
import { Component, Inject, OnInit } from "@angular/core";
import { PaymentDue } from "@app/services/shared/PaymentDue.type";
import { MatDialog } from "@angular/material/dialog";
import { PaymentDeclinedDialog } from "@app/components/sharedDialogs/dialogPaymentDeclined.component";
import { PaymentErrorDialog } from "@app/components/sharedDialogs/dialogPaymentError.component";
import { Router } from "@angular/router";
import { AppDate } from "@app/util/app-date";
import { Property } from "@app/models/Property";
import { UserStore } from "@app/services/stores/UserStore";
import { OneTimeOverlapDialog } from "@app/pages/payment/onetime-overlap.dialog";
import { PaymentHistoryApi } from "@app/services/apiServices/PaymentHistoryApi";
import { PaymentHistory } from "@app/models/PaymentHistory";
import { ValidPaymentStatusType } from "@app/services/shared/ValidPaymentStatusType";
import { ErrorHandlerService } from "@app/services/ErrorHandler.service";
import { FeatureFlagService } from "@app/services/feature-flag.service";

type PaymentOneTimeRange = {
    date: AppDate;
    amount: number;
    vendorItemNumber: number;
}

/**
 * Created by req89873 on 12/02/16.
 */
@Component({
    selector: 'app-payment-confirmation',
    template: require('./paymentconfirmation.component.html')
})
export class PaymentConfirmationComponent implements OnInit {
    paymentRequest: PaymentRequest;
    email: string;
    tcCheckbox: boolean;
    $stateParams: any;
    disableSubmitBtn: boolean = false;
    duplicateCheck: boolean = false;
    isAsyncFeatureEnabled: boolean = false;

    constructor(
        @Inject(MatDialog) private dialog: MatDialog,
        @Inject(PaymentApi) private paymentApi: PaymentApi,
        @Inject(SpinnerService) private spinnerService: SpinnerService,
        @Inject(NetworkErrorService) private networkErrorService: NetworkErrorService,
        @Inject(ErrorHandlerService) private errorHandlerService: ErrorHandlerService,
        @Inject(PaymentStore) private paymentStore: PaymentStore,
        @Inject(UserStore) private userStore: UserStore,
        @Inject(Router) private router: Router,
        @Inject(PaymentHistoryApi) private paymentHistoryApi: PaymentHistoryApi,
        @Inject(FeatureFlagService) private featureFlagService: FeatureFlagService) {

        if(this.router.getCurrentNavigation()) {
            this.$stateParams = this.router.getCurrentNavigation().extras.state;
        }

    }

    ngOnInit() {

        this.isAsyncFeatureEnabled = this.featureFlagService.isAsyncFeatureEnabled();

        if (this.$stateParams && this.$stateParams.paymentRequest) {
            this.paymentRequest = this.$stateParams.paymentRequest;
        } else if (this.paymentStore.getPaymentRequest()) {
            this.paymentRequest = this.paymentStore.getPaymentRequest();
        } else {
            this.router.navigateByUrl('/properties');
        }

        if (this.$stateParams && this.$stateParams.email) {
            this.email = this.$stateParams.email;
        }
    }

    /**
     * Retrieves property list data for the current user.
     *
     * @returns {promise<property[]>} A promise that resolves to an array of properties.
     */
    getPropertiesData(): Promise<Property[]> {
        return new Promise<Property[]>((resolve, reject) => {
            this.paymentHistoryApi.propertiesIdPaymentHistoriesGet(this.paymentRequest.property.id).pipe(take(1))
                .subscribe({
                    next: (resp) => {
                        resolve(resp);
                    }, error: (err) => {
                        if (err.status === 404) {
                            resolve([]);
                        }
                        reject();
                    }
                });
        });
    }

    /**
   * Checks for overlapping payments in the user's payment history.
   *
   * @param {boolean} value - A boolean value
   * @returns {promise<boolean>}A promise that resolves to true if overlapping payments are found , otherwise false.
   *
   */
    checkForOverlap(): Promise<boolean> {
        return this.getPropertiesData()
            .then((paymentHistory: PaymentHistory[]) => {
                // Early return if no payment history
                if (!paymentHistory.length) {
                    return true;
                }

                const endDate = AppDate.fromString(this.paymentRequest.acceptedTermsDate, 'yyyy-MM-dd');
                const startDate = endDate.subrtactMinutes(1440);
                const amount = this.paymentRequest.payment.principalAmount;

                const overlappingPayment = paymentHistory.find(payment => {
                    const oneTimeDate = AppDate.fromString(payment.date.formattedFullDate, 'MM/dd/yyyy');

                    return startDate.fullIndex <= oneTimeDate.fullIndex &&
                        endDate.fullIndex >= oneTimeDate.fullIndex &&
                        payment.principalAmount === amount &&
                        payment.paymentEntityType === this.paymentRequest.paymentEntityType &&
                        Object.values(ValidPaymentStatusType).includes(payment.status as ValidPaymentStatusType);
                });

                if (overlappingPayment) {
                    const oneTimeDate = AppDate.fromString(overlappingPayment.date.formattedFullDate, 'MM/dd/yyyy');
                    const dialogRef = this.dialog.open(OneTimeOverlapDialog, {
                        data: {
                            currentDate: oneTimeDate,
                            amount: overlappingPayment.amount,
                            vendorItemNumber: overlappingPayment.vendorItemNumber
                        }
                    });

                    // Return the dialog result directly
                    return dialogRef.afterClosed()
                        .pipe(take(1))
                        .toPromise()
                        .then(value => {
                            this.duplicateCheck = !!value;
                            return value;
                        });
                }
                return true;
            });
    }
    /**
     * Submits the payment after checking for overlapping payments.
     *
     * @param {boolean} result - the result of the checkforOverlap method.
     */
    submit() {
        this.disableSubmitBtn = true;

        // Check for overlapping payments
        this.checkForOverlap().then((canProceed) => {
            // If we can't proceed with payment, go back to properties page
            if (!canProceed) {
                this.goToHOAPay();
                return;
            }

            const today = AppDate.now()
            this.paymentRequest.acceptedTerms = this.tcCheckbox;
            this.paymentRequest.acceptedTermsDate = today.format("yyyy-MM-dd");

            this.paymentRequest.correlationId = this.paymentStore.getCorrelationId();
            this.paymentRequest.duplicateCheck = this.duplicateCheck;

            if (this.paymentRequest.futureDatedPayment) {
                console.error("Cannot submit future dated payment in registered");
                return;
            }

            this.spinnerService.showSpinner();

            this.paymentApi.paymentsPost(false, this.paymentRequest)
                .pipe(take(1))
                .subscribe({
                    next: (data) => {
                        this.spinnerService.closeSpinner();

                        // Check feature flag to determine which payment logic to use
                        if (this.isAsyncFeatureEnabled) {
                            // Validate the response data
                            if (data.message === "Payment accepted") {
                                this.handleAsyncSuccessfulPayment(data);
                            } else {
                                // Handle any non-success response as an error
                                console.error("PaymentApi paymentsPost unexpected response", data);
                                this.handlePaymentError({ message: "Payment failed" });
                            }
                        } else {
                            if (data.userResponseCode === "APPROVED") {
                                this.handleSuccessfulPayment(data);
                            } else {
                                this.handleDeclinedPayment(
                                    data.userResponseMessage
                                );
                            }
                        }
                    },
                    error: (err) => {
                        this.spinnerService.closeSpinner();
                        this.handlePaymentError(err);
                    }
                });
        });
    }

    goToHOAPay() {
        this.router.navigateByUrl('/properties');
    }

    filteredDues(dues: PaymentDue[]) {
        if (dues) {
            return dues.filter(due => due.toPay === true)
        }
    }

    navigateToPropertiesDummy() {
        this.paymentStore.clearCorrelationId();
        this.router.navigateByUrl('/properties/' + this.paymentRequest.property.id + '/payment',
            { state: { propertyId: this.paymentRequest.property.id } })
    }

    private handleAsyncSuccessfulPayment(data: any) {
        this.paymentStore.clearAll();
        this.paymentStore.setPaymentSuccessInfo({
            paymentId: data.id,
            paymentType: "payment",
            paymentRequest: this.paymentRequest,
            totalPaymentAmount: this.paymentRequest.payment.totalPaymentAmount,
            paymentDate: this.paymentRequest.paymentDate.formattedFullDate as any,
            email: this.email,
            confirmationNumber: null,
            property: this.paymentRequest.property
        });

        this.router.navigateByUrl('/properties/' + this.paymentRequest.property.id + '/paymentSuccess',
            { state: { propertyId: this.paymentRequest.property.id, isAsyncFlow: true } });
    }

    private handleSuccessfulPayment(data: any) {
        this.paymentStore.clearAll();
        this.paymentStore.setPaymentSuccessInfo({
            paymentId: data.id,
            paymentType: "payment",
            paymentRequest: this.paymentRequest,
            totalPaymentAmount: this.paymentRequest.payment.totalPaymentAmount,
            paymentDate: this.paymentRequest.paymentDate.formattedFullDate as any,
            email: this.email,
            confirmationNumber: data.confirmationNumber,
            property: this.paymentRequest.property
        });

        this.router.navigateByUrl('/properties/' + this.paymentRequest.property.id + '/paymentSuccess',
            { state: { propertyId: this.paymentRequest.property.id } });
    }

    private handleDeclinedPayment(errorMessage: string) {
        const dialogRef = this.dialog.open(PaymentDeclinedDialog, {
            data: { errorMessage: errorMessage }
        });

        dialogRef.afterClosed().pipe(take(1)).subscribe(() => {
            this.goToHOAPay();
        });
    }

    private handlePaymentError(err: any) {
        this.spinnerService.closeSpinner();

        if (err.status === 409) {
            this.errorHandlerService.showPaymentErrorDialog(
                'Your payment was not processed.',
                'Not enough inventory to complete the request.',
                'Click OK to go back and correct any problems with your request.'
            ).subscribe(() => {
                this.router.navigateByUrl('/properties/' + this.paymentRequest.property.id + '/payment',
                    { state: {
                        propertyId: this.paymentRequest.property.id,
                        property: this.paymentRequest.property
                    }
                    });
            });
        } else {
            // Generic payment error
            const dialogRef = this.dialog.open(PaymentErrorDialog);

            dialogRef.afterClosed().pipe(take(1)).subscribe(() => {
                this.goToHOAPay();
            });
        }
    }

    //todo: handle going back to the payment setup screen.

}

